import { useNavigate } from "react-router-dom";
import {
    Home,
    ArrowUpRight,
    Check,
    Sparkles,
    BadgeCheck,
    Star,
    LifeBuoy,
    ShoppingCart,
    Info,
} from "lucide-react";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

const packages = [
    {
        id: "landing-page",
        name: "Professional Landing Page",
        tagline: "One page. Purpose-built to convert.",
        price: "$300",
        billing: "one-time build",
        featured: false,
        testId: "package-landing-page",
        summary:
            "A professionally designed, high-converting landing page built to showcase your business, generate leads, and turn visitors into customers. Fully responsive, fast-loading, and customized to match your brand.",
        features: [
            "Single-page custom design tailored to your brand",
            "Conversion-focused layout — hero, benefits, proof, CTA",
            "Lead capture form built into the page",
            "Fully responsive across every device",
            "Fast-loading, performance-optimized build",
            "Basic on-page SEO — meta tags, titles, clean markup",
        ],
        primaryCta: "Purchase",
        stripeUrl: "https://buy.stripe.com/3cIfZibM30HR6Zm4tS1Nu06",
    },
    {
        id: "starter",
        name: "Starter Website",
        tagline: "The essentials, professionally built.",
        price: "$800",
        billing: "one-time build",
        featured: false,
        testId: "package-starter",
        summary:
            "A clean, mobile-ready website with everything a small business needs to look credible and get found — designed and launched by our team.",
        features: [
            "3 to 5 fully custom pages",
            "Mobile-responsive design across every device",
            "Contact form built into the site",
            "Basic on-page SEO — meta tags, page titles, clean markup",
            "Google Maps integration to help customers find you",
        ],
        primaryCta: "Purchase",
        stripeUrl: "https://buy.stripe.com/00w00kbM3duD0AYf8w1Nu00",
    },
    {
        id: "professional",
        name: "Professional Business Site",
        tagline: "Everything you need to grow online.",
        price: "$1,600",
        billing: "one-time build",
        featured: true,
        testId: "package-professional",
        summary:
            "A full-featured business website with the tools you need to attract, convert, and stay in front of your customers — built to scale as your business grows.",
        featuresIntro: "Includes everything in Starter, plus:",
        features: [
            "5 to 10 fully custom pages",
            "Booking or quote request forms",
            "Integrated blog for content and updates",
            "Customer reviews section",
            "Website analytics setup",
            "Advanced SEO optimization",
            "Optional AI chatbot integration — FAQs, lead qualification, and site navigation assistance",
        ],
        primaryCta: "Purchase",
        stripeUrl: "https://buy.stripe.com/28EbJ2aHZeyH1F2bWk1Nu03",
    },
    {
        id: "care-plan",
        name: "Monthly Care Plan",
        tagline: "Ongoing support to keep your site running.",
        price: "$250",
        billing: "per month · add-on",
        featured: false,
        testId: "package-care-plan",
        summary:
            "Add continuous care to any RecoveryDesk website. We handle the technical upkeep so you can focus entirely on running your business.",
        features: [
            "Managed website hosting",
            "Security updates and patches",
            "Content changes and small edits",
            "Regular automated backups",
            "Performance monitoring and speed checks",
        ],
        primaryCta: "Add To My Site",
        stripeUrl: "https://buy.stripe.com/bJe9AU4jBduD4ReaSg1Nu04",
    },
];

export default function PackagesPage() {
    const navigate = useNavigate();

    const goHome = () => navigate("/");
    const openStripe = (url) => {
        if (!url || url.includes("REPLACE_ME")) return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <main
            data-testid="packages-page"
            className="relative min-h-screen text-white font-body antialiased"
        >
            <Navbar />

            {/* Hero */}
            <section className="relative pt-36 pb-16 luxe-radial overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                    <button
                        data-testid="packages-home-btn"
                        onClick={goHome}
                        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C4C4C4] hover:text-[#D4AF37] transition-colors duration-300 mb-12"
                    >
                        <Home className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Return Home
                    </button>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="gold-rule-solid w-10" />
                        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">
                            Packages
                        </span>
                    </div>

                    <h1
                        data-testid="packages-headline"
                        className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight text-white font-light max-w-5xl"
                    >
                        Straightforward pricing.
                        <br />
                        <span className="italic text-gold-metallic">
                            Built to fit your business.
                        </span>
                    </h1>

                    <p className="mt-8 max-w-2xl text-base md:text-lg font-light leading-relaxed text-[#C4C4C4]">
                        Choose the website that matches where your business is
                        today, and add ongoing care whenever you&apos;re ready.
                        Every RecoveryDesk build is custom-designed and
                        professionally launched by our team.
                    </p>
                </div>
            </section>

            {/* Pricing grid */}
            <section className="relative pb-16 luxe-radial-faint">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                    <div
                        data-testid="packages-grid"
                        className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8"
                    >
                        {packages.map((pkg) => (
                            <article
                                key={pkg.id}
                                data-testid={pkg.testId}
                                        className={`relative bg-[#050505] p-8 md:p-10 flex flex-col ${
                                    pkg.featured
                                        ? "border border-[#D4AF37] glow-gold xl:-mt-4 xl:mb-4"
                                        : "border border-white/[0.08]"
                                }`}
                            >
                                {/* Corner accents on featured */}
                                {pkg.featured ? (
                                    <>
                                        <div className="absolute top-0 left-0 w-10 h-px bg-[#D4AF37]" />
                                        <div className="absolute top-0 left-0 h-10 w-px bg-[#D4AF37]" />
                                        <div className="absolute top-0 right-0 w-10 h-px bg-[#D4AF37]" />
                                        <div className="absolute top-0 right-0 h-10 w-px bg-[#D4AF37]" />
                                        <div className="absolute bottom-0 left-0 w-10 h-px bg-[#D4AF37]" />
                                        <div className="absolute bottom-0 left-0 h-10 w-px bg-[#D4AF37]" />
                                        <div className="absolute bottom-0 right-0 w-10 h-px bg-[#D4AF37]" />
                                        <div className="absolute bottom-0 right-0 h-10 w-px bg-[#D4AF37]" />
                                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] font-bold inline-flex items-center gap-2">
                                            <Star className="w-3 h-3" strokeWidth={2.5} />
                                            Most Popular
                                        </div>
                                    </>
                                ) : null}

                                <div className="flex items-center gap-2 mb-4">
                                    {pkg.id === "care-plan" ? (
                                        <LifeBuoy
                                            className="w-4 h-4 text-[#D4AF37]"
                                            strokeWidth={1.75}
                                        />
                                    ) : (
                                        <BadgeCheck
                                            className="w-4 h-4 text-[#D4AF37]"
                                            strokeWidth={1.75}
                                        />
                                    )}
                                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-semibold">
                                        {pkg.id === "care-plan"
                                            ? "Optional Add-On"
                                            : "Website Package"}
                                    </span>
                                </div>

                                <h2 className="font-display text-3xl md:text-[34px] text-white leading-tight tracking-tight">
                                    {pkg.name}
                                </h2>
                                <p className="mt-2 text-sm text-[#888] font-light italic">
                                    {pkg.tagline}
                                </p>

                                <div className="mt-8 pb-8 border-b border-white/[0.08]">
                                    <div className="font-display text-5xl md:text-6xl text-gold-metallic leading-none">
                                        {pkg.price}
                                        <span className="align-top ml-1 text-3xl md:text-4xl text-[#D4AF37]/80">+</span>
                                    </div>
                                    <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-[#888]">
                                        {pkg.billing}
                                    </div>
                                </div>

                                <p className="mt-6 text-sm text-[#C4C4C4] font-light leading-relaxed">
                                    {pkg.summary}
                                </p>

                                {pkg.featuresIntro ? (
                                    <p className="mt-6 text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">
                                        {pkg.featuresIntro}
                                    </p>
                                ) : null}

                                <ul className="mt-6 space-y-3 flex-grow">
                                    {pkg.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3"
                                        >
                                            <Check
                                                className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5"
                                                strokeWidth={2}
                                            />
                                            <span className="text-sm text-[#C4C4C4] font-light leading-relaxed">
                                                {f}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-10 flex flex-col gap-3">
                                    <button
                                        data-testid={`${pkg.testId}-cta`}
                                        onClick={() => openStripe(pkg.stripeUrl)}
                                        className={`group inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-semibold transition-all duration-300 ${
                                            pkg.featured
                                                ? "bg-[#D4AF37] text-black hover:bg-[#E5C158] btn-gold-hover"
                                                : "border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                                        }`}
                                    >
                                        {pkg.id === "care-plan" ? (
                                            <LifeBuoy className="w-3.5 h-3.5" strokeWidth={2} />
                                        ) : (
                                            <ShoppingCart className="w-3.5 h-3.5" strokeWidth={2} />
                                        )}
                                        {pkg.primaryCta}
                                        <ArrowUpRight
                                            className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                            strokeWidth={2}
                                        />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Continuous upgrades notice */}
            <section className="relative pb-24 md:pb-32 luxe-radial-faint">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
                    {/* Pricing disclaimer */}
                    <aside
                        data-testid="pricing-disclaimer"
                        className="relative border border-[#D4AF37]/30 bg-black/50 backdrop-blur-sm p-8 md:p-10"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <Info
                                className="w-4 h-4 text-[#D4AF37]"
                                strokeWidth={1.5}
                            />
                            <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
                                Pricing Disclaimer
                            </span>
                        </div>
                        <p className="text-sm md:text-base text-[#C4C4C4] font-light leading-relaxed max-w-3xl">
                            The prices listed above represent
                            <span className="text-white"> baseline starting rates </span>
                            for each package. Final pricing may vary based on
                            project scope, workload, and any additional features
                            or customizations requested beyond what is included
                            in the standard package description. A precise,
                            transparent quote will be provided during your
                            discovery consultation before any work begins.
                        </p>
                    </aside>

                    {/* Reassurance tiles */}
                    <div className="mt-12 grid sm:grid-cols-3 gap-px bg-[#D4AF37]/10">
                        <div className="bg-black p-8">
                            <Check
                                className="w-5 h-5 text-[#D4AF37] mb-4"
                                strokeWidth={1.5}
                            />
                            <div className="font-display text-xl text-white mb-2">
                                Live In Days
                            </div>
                            <p className="text-xs text-[#888] font-light leading-relaxed">
                                From kickoff to a published, professional
                                website — fast.
                            </p>
                        </div>
                        <div className="bg-black p-8">
                            <Check
                                className="w-5 h-5 text-[#D4AF37] mb-4"
                                strokeWidth={1.5}
                            />
                            <div className="font-display text-xl text-white mb-2">
                                No Long Contracts
                            </div>
                            <p className="text-xs text-[#888] font-light leading-relaxed">
                                Care Plan is month-to-month. Cancel anytime.
                            </p>
                        </div>
                        <div className="bg-black p-8">
                            <Check
                                className="w-5 h-5 text-[#D4AF37] mb-4"
                                strokeWidth={1.5}
                            />
                            <div className="font-display text-xl text-white mb-2">
                                White-Glove Service
                            </div>
                            <p className="text-xs text-[#888] font-light leading-relaxed">
                                We handle the tech so you can focus on your
                                business.
                            </p>
                        </div>
                    </div>

                    {/* Bottom home button */}
                    <div className="mt-16 flex justify-center">
                        <button
                            data-testid="packages-home-btn-bottom"
                            onClick={goHome}
                            className="group inline-flex items-center gap-3 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] hover:border-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
                        >
                            <Home className="w-4 h-4" strokeWidth={1.5} />
                            Return Home
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
